import { useQuery } from '@tanstack/react-query';
import { client } from '@/lib/api';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

/**
 * Matching health (PRD §F4 targets).
 *
 * This is a quality metric surface, not decoration. It is how a planner — or
 * whoever is supporting them — finds out that matching has degraded, and it is
 * the only place the two numbers the PRD sets targets against are visible:
 *
 *  - **Queue rate** under ~20%. Above that the review queue stops being an
 *    exception and becomes a second inbox, which is the thing this product
 *    exists to remove.
 *  - **Deterministic rate** at 85% or better. Tiers 1–3 are thread ids and
 *    header chains: no guessing, no model. When that share falls, the matching
 *    is leaning on inference it should not need to.
 *
 * Both are computed over the last 30 days of decisions.
 */

interface Health {
  windowDays: number;
  matched: number;
  review: number;
  ignored: number;
  queueRate: number;
  deterministicRate: number;
  byTier: Record<string, number>;
}

const TIER_LABELS: Record<string, string> = {
  '1': 'Gmail thread',
  '2': 'Reply headers',
  '3': 'Only open inquiry',
  '4': 'Classifier',
};

export default function MatchHealthPanel() {
  const { data, isLoading } = useQuery<Health>({
    queryKey: ['match-health'],
    queryFn: async () => {
      const res = await client.api.gmail['match-health'].$get();
      if (!res.ok) throw new Error('Failed to load matching health');
      return res.json();
    },
  });

  const decided = (data?.matched ?? 0) + (data?.review ?? 0);

  return (
    <Card className="rounded-3xl bg-card shadow-ambient">
      <CardHeader className="pb-2">
        <h2 className="label-caps text-xs text-muted-foreground">Matching health</h2>
        <p className="text-sm text-muted-foreground">
          How well replies filed themselves over the last {data?.windowDays ?? 30} days.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : decided === 0 ? (
          /* 0 of 0 is not 0% — showing a red "0%" against a target would send
             someone hunting a problem that does not exist yet. */
          <p className="rounded-2xl bg-[var(--color-surface-container-high)] px-4 py-3 text-sm text-muted-foreground">
            Not enough data yet. Once vendors start replying, this will show how many replies file
            themselves and how many need you.
          </p>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <Metric
                label="Sent to review"
                value={data!.queueRate}
                target="under 20%"
                good={data!.queueRate <= 0.2}
                hint={`${data!.review} of ${decided} replies needed you`}
              />
              <Metric
                label="Filed without guessing"
                value={data!.deterministicRate}
                target="85% or better"
                good={data!.deterministicRate >= 0.85}
                hint="Matched on a thread id or reply headers"
              />
            </div>

            <div className="space-y-1">
              <p className="label-caps text-xs text-muted-foreground">How they were matched</p>
              {Object.entries(TIER_LABELS).map(([tier, label]) => (
                <div key={tier} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    Tier {tier} · {label}
                  </span>
                  <span className="tabular-nums">{data!.byTier[tier] ?? 0}</span>
                </div>
              ))}
              <div className="flex justify-between border-t border-border pt-1 text-sm">
                <span className="text-muted-foreground">Ignored (not vendor mail)</span>
                <span className="tabular-nums">{data!.ignored}</span>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Metric({
  label,
  value,
  target,
  good,
  hint,
}: {
  label: string;
  value: number;
  target: string;
  good: boolean;
  hint: string;
}) {
  return (
    <div className="rounded-2xl bg-[var(--color-surface-container-high)] px-4 py-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`display-tight text-2xl font-semibold ${good ? '' : 'text-destructive'}`}>
        {Math.round(value * 100)}%
      </p>
      <p className="text-xs text-muted-foreground">
        {hint} · target {target}
        {!good && ' · off target'}
      </p>
    </div>
  );
}
